import React, { useEffect, useId, useRef } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCheckCircle } from "@fortawesome/free-solid-svg-icons";
import { LoadingSpinner } from "@/components/ui/loading-spinner";
import {
  PAYMENT_PROCESSING_HEADLINE,
  PAYMENT_PROCESSING_SUBTEXT,
  PAYMENT_PROCESSING_SECURITY_HINT,
  PAYMENT_SUCCESS_HEADLINE,
  PAYMENT_SUCCESS_SUBTEXT,
} from "./checkoutPaymentLabels";

export interface PaymentProcessingOverlayProps {
  phase: "processing" | "success";
  packageName?: string;
  orderId?: string | null;
  packageId?: string | null;
}

/**
 * Full-screen card checkout processing overlay.
 * Keyframes mirror CheckoutSuccessPage.tsx (duplicated intentionally — see design Decision 4).
 */
export const PaymentProcessingOverlay: React.FC<PaymentProcessingOverlayProps> = ({
  phase,
}) => {
  const headlineId = useId();
  const overlayRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const previous = document.activeElement as HTMLElement | null;
    overlayRef.current?.focus({ preventScroll: true });
    return () => {
      const brick = document.getElementById("mercadopago-card-adapter");
      if (brick instanceof HTMLElement) {
        brick.focus({ preventScroll: true });
      } else if (previous && typeof previous.focus === "function") {
        previous.focus({ preventScroll: true });
      }
    };
  }, []);

  return (
    <div
      ref={overlayRef}
      id="payment-processing-overlay"
      role="dialog"
      aria-modal="true"
      aria-labelledby={headlineId}
      tabIndex={-1}
      className="fixed inset-0 z-50 flex items-center justify-center bg-background/90 outline-none"
    >
      {/* Keyframes duplicated from CheckoutSuccessPage.tsx — keep in sync */}
      <style>{`
        @keyframes ofc-success-pop {
          0% { transform: scale(0.85); opacity: 0; }
          60% { transform: scale(1.05); }
          100% { transform: scale(1); opacity: 1; }
        }
        @keyframes ofc-success-ring {
          0% { transform: scale(1); opacity: 0.6; }
          100% { transform: scale(1.35); opacity: 0; }
        }
        @keyframes ofc-success-float {
          0%, 100% { transform: translateY(0); opacity: 0.4; }
          50% { transform: translateY(-6px); opacity: 0.7; }
        }
        .ofc-overlay-success-icon {
          animation: ofc-success-pop 0.5s ease-out forwards;
        }
        .ofc-overlay-success-ring {
          animation: ofc-success-ring 1.2s ease-out 0.2s forwards;
        }
        .ofc-overlay-success-sparkle {
          animation: ofc-success-float 2.5s ease-in-out infinite;
        }
        @media (prefers-reduced-motion: reduce) {
          .ofc-overlay-success-icon,
          .ofc-overlay-success-ring,
          .ofc-overlay-success-sparkle,
          .ofc-overlay-card-enter {
            animation: none !important;
          }
        }
      `}</style>

      <div className="ofc-overlay-card-enter max-w-md w-full mx-4 bg-background rounded-2xl border border p-8 text-center animate-in fade-in zoom-in-95 duration-200">
        <div aria-live="polite" className="min-h-[11rem]">
          {phase === "processing" ? (
            <div className="flex flex-col items-center gap-4 transition-opacity duration-200">
              <LoadingSpinner size="lg" className="text-success" />
              <h2
                id={headlineId}
                className="text-2xl font-semibold text-foreground"
              >
                {PAYMENT_PROCESSING_HEADLINE}
              </h2>
              <p className="text-sm text-text-secondary">
                {PAYMENT_PROCESSING_SUBTEXT}
              </p>
              <p className="text-xs text-text-tertiary">
                {PAYMENT_PROCESSING_SECURITY_HINT}
              </p>
            </div>
          ) : (
            <div className="flex flex-col items-center gap-4 relative overflow-hidden transition-opacity duration-200">
              <div
                className="pointer-events-none absolute inset-0 opacity-30"
                aria-hidden="true"
              >
                <span className="ofc-overlay-success-sparkle absolute top-2 left-10 h-2 w-2 rounded-full bg-success" />
                <span
                  className="ofc-overlay-success-sparkle absolute top-6 right-12 h-1.5 w-1.5 rounded-full bg-success-subtle"
                  style={{ animationDelay: "0.4s" }}
                />
              </div>

              <div className="mb-1 flex justify-center relative">
                <span
                  className="ofc-overlay-success-ring absolute h-20 w-20 rounded-full border-2 border-success"
                  aria-hidden="true"
                />
                <div className="ofc-overlay-success-icon bg-success-muted rounded-full p-5 relative">
                  <FontAwesomeIcon
                    icon={faCheckCircle}
                    className="text-5xl text-success"
                  />
                </div>
              </div>

              <h2
                id={headlineId}
                className="text-2xl md:text-3xl font-semibold text-foreground"
              >
                {PAYMENT_SUCCESS_HEADLINE}
              </h2>
              <p className="text-base text-text-secondary">
                {PAYMENT_SUCCESS_SUBTEXT}
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
